/**
 * Centralizovaný manažér jazyka
 * Zabezpečuje konzistentný jazyk v celej aplikácii
 */

import i18n from './i18n';

export type SupportedLanguage = 'sk' | 'en';

class LanguageManager {
  private currentLanguage: SupportedLanguage = 'sk';
  private isInitialized = false;

  /**
   * Inicializácia manažéra jazyka
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Načítame podporované jazyky
      await i18n.loadLanguages(['sk', 'en']);
      
      // Načítame všetky potrebné namespaces
      await i18n.loadNamespaces([
        'common', 'reminders', 'tasks', 'invoices', 'clients', 
        'profile', 'nav', 'plans', 'pricing', 'notifications',
        'filters', 'modals', 'admin', 'billing'
      ]);

      // Nastavíme počiatočný jazyk
      const savedLanguage = localStorage.getItem('i18nextLng') as SupportedLanguage;
      const browserLanguage = navigator.language?.startsWith('sk') ? 'sk' : 'en';
      const initialLanguage = savedLanguage || browserLanguage;

      await this.setLanguage(initialLanguage);
      this.isInitialized = true;
    } catch (error) {
      console.error('Error initializing language manager:', error);
      // Fallback na slovenčinu
      await this.setLanguage('sk');
    }
  }

  /**
   * Nastavenie jazyka
   */
  async setLanguage(language: SupportedLanguage): Promise<void> {
    if (this.currentLanguage === language) return;

    try {
      await i18n.changeLanguage(language);
      this.currentLanguage = language;
      
      // Uložíme do localStorage
      localStorage.setItem('i18nextLng', language);
      
      // Aktualizujeme HTML lang atribút
      document.documentElement.lang = language;
      
      console.log(`Language changed to: ${language}`);
    } catch (error) {
      console.error(`Error changing language to ${language}:`, error);
    }
  }

  /**
   * Získanie aktuálneho jazyka
   */
  getCurrentLanguage(): SupportedLanguage {
    return this.currentLanguage;
  }

  /**
   * Kontrola, či je manažér inicializovaný
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Nastavenie jazyka na základe používateľa
   */
  async setLanguageFromUser(userLocale?: string): Promise<void> {
    if (!userLocale) return;

    const language = userLocale.startsWith('sk') ? 'sk' : 'en';
    await this.setLanguage(language);
  }

  /**
   * Získanie zoznamu podporovaných jazykov
   */
  getSupportedLanguages(): SupportedLanguage[] {
    return ['sk', 'en'];
  }

  /**
   * Kontrola, či je jazyk podporovaný
   */
  isLanguageSupported(language: string): language is SupportedLanguage {
    return ['sk', 'en'].includes(language);
  }
}

// Singleton inštancia
export const languageManager = new LanguageManager();

// Export pre jednoduché použitie
export const setLanguage = (language: SupportedLanguage) => languageManager.setLanguage(language);
export const getCurrentLanguage = () => languageManager.getCurrentLanguage();
export const isLanguageReady = () => languageManager.isReady();

